// src/prototype/enterprise/governance/governanceModel.ts
// ============================================================
// ENTERPRISE · GOVERNANCE FRAMING (design "1j") — the "route to governance" queue.
//
// Builds the ratification queue for the saved Q2 scenario from the spine (read-only): the Q2
// decisions on the record, each with the REAL CFO capital-gate check (its capital_ask vs the
// capital still remaining under the $40M cap above its rank → fits yes/no) and its recorded
// rationale. The approval chain uses the real ApprovalChain component's ApprovalStep shape.
// ============================================================

import { getScenarios, getRuns, getPortfolioAsOf, getCapitalAsks } from '../../data/adapters/enterpriseSpine';
import { buildDecisionTimeline, type DecisionKind } from '../decisions/decisionsModel';
import type { ApprovalStep } from '../../data/executionInitiatives';

export interface GovItem {
  useCaseId: number;
  title: string;
  rank: number;
  funded: boolean;
  composite: number;
  ask: number; // $M
  remaining: number; // $M still available under the cap above this initiative's rank
  cap: number; // $M
  fits: boolean;
  recordedDecision: DecisionKind;
  recordedBy: string;
  rationale: string;
  stages: ApprovalStep[];
}

export interface GovernanceQueue {
  found: boolean;
  scenarioName: string;
  cap: number;
  items: GovItem[];
}

/** The 4-stage approval chain (real ApprovalChain shape); CFO gate is the current stage. */
function stagesFor(): ApprovalStep[] {
  return [
    { label: 'Submitted', state: 'done', note: 'D. Whitfield · Client Admin' },
    { label: 'Committee review', state: 'done', note: 'Enterprise Strategy Committee' },
    { label: 'CFO capital gate', state: 'current', cfo: true, note: 'Morgan Ellis · CFO' },
    { label: 'Logged to record', state: 'pending', note: 'decision_log' },
  ];
}

export function buildGovernanceQueue(): GovernanceQueue {
  const scen = getScenarios().find((s) => s.name === 'Q2 Budget Reduction');
  if (!scen) return { found: false, scenarioName: '—', cap: 0, items: [] };
  const cap = scen.levers.budget_cap; // $40M
  const q2Run = getRuns().find((r) => r.scenario_id === scen.scenario_id);

  // Source rank/funded from the Q2 RUN — the authored result the recorded decisions were made
  // against — so the CFO capital check is consistent with each decision (funded ⇒ fits).
  const asof = getPortfolioAsOf(q2Run?.run_id ?? '');
  const initiatives = asof?.initiatives ?? [];
  const asks = getCapitalAsks();

  // Capital committed by FUNDED initiatives ranked above each initiative (its gate headroom).
  const committedAbove = new Map<number, number>();
  let cum = 0;
  for (const row of [...initiatives].sort((a, b) => a.rank - b.rank)) {
    committedAbove.set(row.use_case_id, cum);
    if (row.funded) cum = Math.round((cum + (asks[row.use_case_id] ?? 0)) * 10) / 10;
  }

  const rowById = new Map(initiatives.map((r) => [r.use_case_id, r]));
  const q2Decisions = buildDecisionTimeline().filter((e) => e.kind === 'decision' && e.run_id === q2Run?.run_id);

  const items: GovItem[] = q2Decisions
    .map((d) => {
      const row = rowById.get(d.use_case_id);
      const ask = asks[d.use_case_id] ?? 0;
      const remaining = Math.round((cap - (committedAbove.get(d.use_case_id) ?? 0)) * 10) / 10;
      return {
        useCaseId: d.use_case_id as number,
        title: d.title,
        rank: row?.rank ?? 0,
        funded: row?.funded ?? false,
        composite: row?.composite ?? 0,
        ask,
        remaining,
        cap,
        fits: remaining >= ask,
        recordedDecision: d.decision,
        recordedBy: d.by,
        rationale: d.rationale,
        stages: stagesFor(),
      };
    })
    .sort((a, b) => a.rank - b.rank); // worked example opp-1 (near the top) first

  return { found: true, scenarioName: scen.name, cap, items };
}
